import React from "react";
import type { FlowStep } from "../scenario/torbridge.js";

export interface FlowConfig {
  seqLabel: string;
  steps: FlowStep[];
}

// The Overview process strip: a slim left-to-right pipeline showing what
// actually happened — invoice → inbox → agent → sealed receipt → verify. The
// first steps are the ordinary agent workflow; the `seq` steps are tinted
// because that is the evidence layer Sequesign adds. Reskinnable: the steps and
// labels come from the scenario, so another demo can supply its own flow.
export function ProcessFlow({ flow }: { flow: FlowConfig }) {
  return (
    <div className="flow-block" aria-label="How it works">
      <ol className="flow">
        {flow.steps.map((step, i) => (
          <React.Fragment key={step.icon + i}>
            {i > 0 && <Arrow />}
            <li className={"flow-node" + (step.seq ? " seq" : "") + (step.verify ? " verify" : "")}>
              <span className="flow-icon" aria-hidden="true">
                {ICONS[step.icon]}
              </span>
              <span className="flow-txt">
                <span className="flow-label">{step.label}</span>
                <span className="flow-sub">{step.sub}</span>
              </span>
            </li>
          </React.Fragment>
        ))}
      </ol>
      <div className="flow-bracket">
        <span>{flow.seqLabel}</span>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <span className="flow-conn" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h13M13 6l6 6-6 6" />
      </svg>
    </span>
  );
}

// Simple inline line glyphs (no external assets — the demo's CSP blocks them),
// stroked with currentColor so they pick up each node's tint.
const ICONS: Record<FlowStep["icon"], React.ReactNode> = {
  invoice: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6M9 16h6M9 8h2" />
    </svg>
  ),
  inbox: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 13l3-8h12l3 8" />
      <path d="M3 13v6h18v-6" />
      <path d="M3 13h5l1.5 2.5h5L15 13h5" />
    </svg>
  ),
  agent: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="8" width="14" height="11" rx="2.5" />
      <path d="M12 8V4M12 4h-.01" />
      <circle cx="9.5" cy="13" r="1" />
      <circle cx="14.5" cy="13" r="1" />
      <path d="M2.5 12v3M21.5 12v3" />
    </svg>
  ),
  seal: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z" />
      <path d="M9.2 12l2 2 3.6-3.8" />
    </svg>
  ),
  verify: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M8 11l2 2 3.5-3.8" />
      <path d="M16 16l5 5" />
    </svg>
  )
};
